import * as React from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type {
  CreateDocumentsRequest,
  OnboardingDocumentRequirement,
  OnboardingDocumentRequirementResult
} from '@gcv/shared';
import { useMediaQuery } from '@mui/material';

import { ReactComponent as CirclePlus } from 'assets/images/ic-circle-plus.svg';
import { useComponent } from 'hooks';
import { useInjection } from 'ioc';
import { Button, Container, ContainerItem } from 'ui';
import { OnboardingPresenter } from 'ui/apps/crb/onboarding-two/onboarding.presenter';
import { UploadDocumentModalV2 } from 'ui/organisms/UploadDocumentsModal/UploadDocumentModalV2';
import { theme } from 'ui/theme';
import { AddCommentModal } from './add-comment-modal';

export const UploadFileButton: React.FC<{
  requirement: OnboardingDocumentRequirement;
  result?: OnboardingDocumentRequirementResult;
}> = useComponent((props) => {
  const presenter = useInjection(OnboardingPresenter);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const form = useForm({ defaultValues: { doNotHave: false } as FieldValues });
  const filesUploaded = props.result?.documents && Object.keys(props.result?.documents).length > 0;

  const [fileUploadOpen, setFileUploadOpen] = React.useState(false);
  const [addCommentModalOpen, setAddCommentModalOpen] = React.useState(false);

  return (
    <Container align="center" justify="center" padding={0} nowrap>
      <ContainerItem minWidth="125px" justify={filesUploaded ? 'center' : 'right'}>
        <Button
          label={filesUploaded ? 'Add ' : 'Upload Files'}
          color={filesUploaded ? 'primary-text' : 'primary'}
          onClick={() => setFileUploadOpen(true)}
          startIcon={filesUploaded && <CirclePlus />}
          dataCy="upload-file-button"
          style={{ padding: isMobile ? '0px 8px' : '6px 16px', fontSize: isMobile ? '12px' : '13px' }}
        />
      </ContainerItem>

      {!props.result || (props.result && Object.keys(props.result.documents).length === 0) ? (
        <>
          <ContainerItem padding="8px">or</ContainerItem>
          <ContainerItem margin="8px 0" minWidth="141px">
            <Button
              dataCy="dont-have-button"
              label="I don't have this"
              onClick={() => {
                setAddCommentModalOpen(true);
              }}
              color="primary-outlined"
              style={{ padding: isMobile ? '0px 8px' : '6px 16px', fontSize: isMobile ? '12px' : '13px' }}
            />
          </ContainerItem>
        </>
      ) : null}

      <UploadDocumentModalV2
        dispensary={presenter.crbDispensaryStore.currentDispensary}
        companyType={'dispensary'}
        addDocumentsToRequirement={async (documentsRequest: CreateDocumentsRequest) => {
          presenter.addDocumentsToRequirement(props.requirement, documentsRequest);
        }}
        openDocumentNewWindow={presenter.openDocument}
        requirement={props.requirement}
        userMap={presenter.viewModel.dispensaryStaff}
        modalOpen={fileUploadOpen}
        toggleModal={setFileUploadOpen}
        sharedRootDocs={presenter.viewModel.sharedRootDocs}
        templateResults={presenter.viewModel.templateResults}
        allDocumentsMap={presenter.viewModel.allDocumentsMap}
      />

      <AddCommentModal
        requirement={props.requirement}
        result={props.result}
        title="Add Explanation"
        addCommentModalOpen={addCommentModalOpen}
        onClose={() => {
          setAddCommentModalOpen(false);
          form.setValue('doNotHave', false);
        }}
      />
    </Container>
  );
});
